/**
 * Button — the WSS UI kit button.
 *
 * Five variants and three sizes. Adopted from the ui-ux-for-wss-erp design
 * handoff, re-coloured to WSS's brand tokens (orange `primary`, navy
 * `secondary`).
 */

import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cx } from './cx';

export type ButtonVariant = 'primary' | 'secondary' | 'navy' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Leading element (typically an icon). */
  icon?: ReactNode;
  /** Trailing element. */
  iconRight?: ReactNode;
  type?: 'button' | 'submit' | 'reset';
}

const VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-primary text-white hover:opacity-90 shadow-sm',
  navy: 'bg-secondary text-white hover:opacity-90 shadow-sm',
  secondary:
    'bg-surface border border-outline-variant text-on-surface hover:bg-surface-container-highest',
  ghost: 'text-outline hover:text-on-surface hover:bg-surface-container-highest',
  danger: 'text-red-600 hover:bg-red-50 border border-transparent hover:border-red-200',
};

const SIZES: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-xs gap-1.5 rounded-md',
  md: 'h-9 px-3.5 text-sm gap-2 rounded-lg',
  lg: 'h-10 px-4 text-sm gap-2 rounded-lg',
};

export default function Button({
  children,
  variant = 'secondary',
  size = 'md',
  icon,
  iconRight,
  type = 'button',
  className = '',
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      className={cx(
        'inline-flex items-center justify-center font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      {...rest}
    >
      {icon}
      {children}
      {iconRight}
    </button>
  );
}
